<script setup>
import { onBeforeMount, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import opmStore from '@/lib/storeOwnerProjectManager';
// import LoadingBar from "@/components/interface/LoadingBar.vue";

const route = useRoute();
const router = useRouter();
const store = useStore();

const pageData = ref();
const entityName = ref('');
// const entityPath = ref(null);
const isLoading = ref(true);
const isDeleting = ref(false);
const error = ref(null);

const entityType = computed(() => route.params.entity_type);
const entityId = computed(() => route.params.entity_id);

// // Parse _path to get parent entity
// function getParentFromPath(path) {
//     if (!path) return null;
    
//     const elements = path.split('.');
//     if (elements.length < 4) {
//         // Not enough elements to have a parent
//         return null;
//     }
    
//     // The parent is the second-to-last pair (entityType, id)
//     const parentType = elements[elements.length - 4];
//     const parentId = elements[elements.length - 3];
    
//     return { type: parentType, id: parentId };
// }

onBeforeMount(async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
        // Fetch page data for entity name
        const pageDataResponse = await opmStore.dispatch("getPageData", {
            entityType: entityType.value,
            entityId: entityId.value,
            isMainData: true,
        });

        
        if (pageDataResponse?.success) {
            pageData.value = pageDataResponse.data;
            // Get entity name from pageData
            if (pageData.value?.name?.value) {
                entityName.value = pageData.value.name.value;
            } else if (pageData.value?.first_name?.value) {
                entityName.value = `${pageData.value.first_name.value} ${pageData.value.last_name?.value || ''}`.trim();
            } else {
                entityName.value = `${entityType.value} ${entityId.value}`;
            }
        } else {
            error.value = 'Failed to load entity information.';
        }
        
    } catch (err) {
        error.value = 'Failed to load entity information.';
        console.error('Error loading entity:', err);
    } finally {
        isLoading.value = false;
    }
});

function handleCancel() {
    router.push(`/${entityType.value}/${entityId.value}`);
}

async function handleDelete() {
    isDeleting.value = true;
    error.value = null;
    
    try {
        const response = await opmStore.dispatch("deleteEntity", {
            entityType: entityType.value,
            entityId: entityId.value,
        });
        
        if (response?.success) {
            // Remove the deleted entity from the store
            store.commit('REMOVE_DOCUMENT_FROM_STORE', entityId.value);
            
            if (response.redirectTo) {
                // Hard refresh to the redirect path
                window.location.href = `#${response.redirectTo}`;
            } else {
                // Fallback to home if no redirectTo provided
                router.push('/');
            }
        } else {
            error.value = response?.errorMessage || response?.error?.message || 'Failed to delete entity.';
            isDeleting.value = false;
        }
    } catch (err) {
        error.value = err?.message || 'Failed to delete entity.';
        isDeleting.value = false;
        console.error('Error deleting entity:', err);
    }
}
</script>

<template>
    <!-- <LoadingBar /> -->
    <v-container fluid>
        <v-row v-if="isLoading">
            <v-col>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <span class="ml-4">Loading...</span>
            </v-col>
        </v-row>
        
        <v-row v-else-if="error && !pageData">
            <v-col>
                <v-alert type="error" variant="tonal">
                    {{ error }}
                </v-alert>
                <v-btn class="mt-4" color="primary" @click="handleCancel">
                    Go Back
                </v-btn>
            </v-col>
        </v-row>
        
        <v-row v-else>
            <v-col cols="12">
                <h1>Delete Confirmation</h1>
            </v-col>
            
            <v-col cols="12">
                <v-alert type="warning" variant="tonal" class="mb-4">
                    <strong>Warning:</strong> You are about to delete <strong>{{ entityName }}</strong>. 
                    This action cannot be undone.
                </v-alert>
            </v-col>
            
            <v-col cols="12" v-if="error">
                <v-alert type="error" variant="tonal">
                    {{ error }}
                </v-alert>
            </v-col>
            
            <v-col cols="12" class="d-flex ga-4">
                <v-btn 
                    color="grey" 
                    variant="outlined" 
                    @click="handleCancel"
                    :disabled="isDeleting"
                >
                    Cancel
                </v-btn>
                <v-btn 
                    color="red" 
                    variant="flat" 
                    @click="handleDelete"
                    :loading="isDeleting"
                    :disabled="isDeleting"
                >
                    Delete
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

